import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { ASSETS } from "../lib/assets";

const GALLERY = [
  { type: "video", src: ASSETS.champVideo, label: "Drills" },
  { type: "video", src: ASSETS.legsVideo, label: "Summer Camp", objectPosition: "top" },
  { type: "video", src: ASSETS.drillsVideo, label: "Warm Ups", objectPosition: "top" },
  { type: "video", src: ASSETS.collageVideo, label: "our hovse!", sound: true },
];

const MARQUEE_TEXT = "OUR HOVSE FOUNDATION · MOVE · TRAIN · BUILD · ";

export const Gallery = () => {
  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
      {/* Marquee */}
      <div className="relative overflow-hidden mb-20 select-none">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, i) => (
            <span
              key={i}
              className="font-display text-[15vw] leading-none text-stroke pr-8"
            >
              {MARQUEE_TEXT.repeat(2)}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 03 — In Action</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none max-w-2xl">
              ENERGY. <span className="text-[#C4F542]">CAPTURED.</span>
            </h2>
          </div>
          <p className="text-base text-zinc-400 font-body max-w-md">
            Real moments from our events — sweat, smiles, and the next generation finding their stride.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {GALLERY.map((g, i) => (
            <GalleryItem key={i} item={g} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const GalleryItem = ({ item: g, index: i }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const heightClass = i === 0 ? "h-[520px]" : "h-[250px]";
  const colClass = i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5";
  const style = g.objectPosition ? { objectPosition: g.objectPosition } : undefined;

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // On unmute, ensure playback continues (some browsers pause when user interacts)
    if (!v.muted) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  };

  return (
    <motion.div
      data-testid={`gallery-item-${i}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className={`relative overflow-hidden border border-white/10 group ${colClass}`}
    >
      {g.type === "video" ? (
        <video
          ref={videoRef}
          src={g.src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          style={style}
          className={`w-full object-cover group-hover:scale-110 transition-transform duration-700 ${heightClass}`}
        />
      ) : (
        <img
          src={g.src}
          alt={g.label}
          style={style}
          className={`w-full object-cover group-hover:scale-110 transition-transform duration-700 ${heightClass}`}
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

      {g.type === "video" && g.sound && (
        <button
          data-testid={`gallery-mute-toggle-${i}`}
          onClick={toggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center border border-white/25 bg-black/50 backdrop-blur-sm text-white hover:border-[#C4F542] hover:text-[#C4F542] transition-colors z-10"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}

      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between pointer-events-none">
        <span className="font-display text-2xl md:text-3xl text-white">{g.label}</span>
        <span className="text-xs uppercase tracking-[0.3em] text-[#C4F542]">0{i + 1}</span>
      </div>
    </motion.div>
  );
};
